import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { TaskService } from '../../../services/task.service';
import { Task } from '../../../models/task.model';
import { AlertController, ModalController, NavController } from '@ionic/angular';
import { TaskDetailsModalComponent } from '../../task-details-modal/task-details-modal.component';
import { Priority } from '../../../models/priority.model';
import { TaskCommentReadStateService } from '../../../services/task-comment-read-state.service';

@Component({
    selector: 'app-task',
    templateUrl: './task.component.html',
    styleUrls: ['./task.component.scss'],
    standalone: false
})
export class TaskComponent {
    @Input() public task: Task;
    @Input() public viewCheckbox = true;
    @Input() public viewCreationDate = true;
    @Input() showPriorities: boolean = false;

    /** Whether to show the show/production name (useful when filtering multiple shows) */
    @Input() showShowName: boolean = false;

    /** Whether this component is embedded in a master-detail layout */
    @Input() embedded: boolean = false;

    /** Whether this task is currently selected */
    @Input() isSelected: boolean = false;

    @Output() public taskStatusChanged: EventEmitter<Task> = new EventEmitter<Task>();

    /** Emits when a task is selected in embedded mode */
    @Output() taskSelected = new EventEmitter<number>();
    protected readonly Priority = Priority;
    private readonly navController: NavController = inject(NavController);
    private readonly commentReadState: TaskCommentReadStateService = inject(TaskCommentReadStateService);

    /** True when the task has comments the user hasn't seen yet (device-local). */
    public get hasNewComments(): boolean {
        return this.commentReadState.hasNewComments(this.task);
    }

    // public async navigateToShowreportDetails(
    //   showreport: Showreport
    // ): Promise<void> {
    //   if (showreport) {
    //     await this.navController.navigateRoot(['/showreport', showreport.id]);
    //   }
    private readonly alertController: AlertController = inject(AlertController);

    constructor(private readonly taskService: TaskService, private readonly modalController: ModalController) {
    }

    public async navigateToTaskDetails(): Promise<void> {
        if (this.task?.id) {
            if (this.embedded) {
                this.taskSelected.emit(this.task.id);
            } else {
                await this.navController.navigateRoot(['/tasks', this.task.id]);
            }
        }
    }

    public async showTaskDetails(task: Task) {
        const modal = await this.modalController.create({
            component: TaskDetailsModalComponent,
            componentProps: {
                task
            }
        });
        await modal.present();
        if (task.submittedBy !== null) {

            let alertMessage = `Toegevoegd door ${ task.submittedBy.firstName }`;

            if (task.completedBy !== null) {
                const taskCompletedBy = task.completedBy.firstName ?? 'Onbekend';
                // Format the date from "2023-12-29 17:38:16" to "29-12-2023 17:38"
                const completedDateTime = task.completedDateTime;
                const completedDateTimeFormatted = completedDateTime.substring(8, 10) + '-' + completedDateTime.substring(5, 7) + '-' + completedDateTime.substring(0, 4) + ' om ' + completedDateTime.substring(11, 16);
                alertMessage += `<br><br>Afgevinkt door ${ taskCompletedBy } <br>op ${ completedDateTimeFormatted }`;
            }

        }

        const {data, role} = await modal.onWillDismiss();

        if (role === 'confirm') {
        }
    }

    public getTruncatedDescription(): string {
        const maxLength = 150;
        if (!this.task?.description) {
            return '';
        }
        if (this.task?.description?.length > (maxLength * 1.1)) {
            // It should break off at the end of a word
            const lastSpaceIndex = this.task.description.indexOf(' ', maxLength);
            if (lastSpaceIndex !== -1) {
                return this.task.description.substring(0, lastSpaceIndex) + '...';
            }
            return this.task.description.substring(0, maxLength) + '...';
        }
        return this.task.description;
    }

    public getPriorityColor(priority: Priority): any {
        switch (priority) {
            case Priority.VeryHigh:
                return 'danger';
            case Priority.High:
                return 'warning';
            case Priority.Low:
                return 'medium';
            case Priority.VeryLow:
                return 'light';
            default:
                return 'light';

        }
    }

    public getPriorityString(priority: Priority): string {
        switch (priority) {
            case Priority.VeryHigh:
                return 'Zeer hoge prioriteit';
            case Priority.High:
                return 'Hoge prioriteit';
            case Priority.Medium:
                return '';
            case Priority.Low:
                return 'Lage prioriteit';
            case Priority.VeryLow:
                return 'Zeer lage prioriteit';
            default:
                return '';
        }
    }

    /**
     * Toggles the completed status of a task
     *
     * @param task The task to toggle the completed status of
     */
    protected async toggleTask(event, task: Task): Promise<void> {
        event.preventDefault();

        if (task.completedBy !== null) {
            await this.confirmUncompleteTask(event, task);
        } else {
            await this.handleCompleteTask(event, task);
        }

        this.taskStatusChanged.emit(task);

    }

    protected async confirmUncompleteTask(event, task: Task): Promise<void> {

        const alert = this.alertController.create({
                header: 'Taak heropenen?',
                message: 'Weet je zeker dat je deze taak wilt heropenen?',
                buttons: [
                    {
                        text: 'Annuleren',
                        role: 'cancel',
                        cssClass: 'secondary',
                        handler: async () => {
                            task.completed = false;
                            event.target.checked = true;

                        }
                    }, {
                        text: 'Heropenen',
                        handler: async () => {
                            await this.handleReopenTask(event, task);
                            event.target.checked = false;

                        }
                    }
                ]
            }
        );

        await (await alert).present();

    }

    protected async handleCompleteTask(event, task: Task) {

        task.completed = !task.completed;
        task.completedDateTime = new Date().toDateString();

        try {
            await this.taskService.changeTaskCompletionStatus(task.id, true);
        } catch (e) {
            console.warn('[TaskComponent] handleCompleteTask() - error: ', e);
            event.target.checked = false;
        }
    }

    protected async handleReopenTask(event, task: Task) {

        task.completed = !task.completed;
        task.completedDateTime = null;

        try {
            await this.taskService.changeTaskCompletionStatus(task.id, false);
        } catch (e) {
            console.warn('[TaskComponent] handleCompleteTask() - error: ', e);
            event.target.checked = true;
        }
    }
}
